// Компонент для отображения детального отчёта по проверке карточки
import { useState } from 'react'
import { ComplianceCheck } from '../../types/wildberries'
import { RiskBadge, RiskList } from './RiskBadge'
import {
  Copy, Download, RefreshCw, AlertTriangle, CheckCircle,
  FileText, ChevronDown, ChevronUp, Clock, BookOpen
} from 'lucide-react'
import { toast } from 'sonner'

interface ComplianceReportProps {
  check: ComplianceCheck
  productName?: string
  onRecheck?: () => void
  className?: string
}

export const ComplianceReport: React.FC<ComplianceReportProps> = ({
  check,
  productName,
  onRecheck,
  className = ''
}) => {
  const [expandedSections, setExpandedSections] = useState({
    risks: true,
    recommendations: true,
    violations: true,
    actions: true,
    technical: false
  })

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const handleCopyRecommendations = () => {
    const text = check.recommendations.join('\n• ')
    navigator.clipboard.writeText(text)
    toast.success('Рекомендации скопированы в буфер')
  }

  const handleExportPDF = () => {
    toast.info('Экспорт в PDF будет доступен в следующем обновлении')
    // Здесь будет логика генерации PDF
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getScoreColor = (score: number) => {
    if (score >= 0.9) return 'text-green-600'
    if (score >= 0.7) return 'text-yellow-600'
    if (score >= 0.5) return 'text-orange-600'
    return 'text-red-600'
  }

  const getScoreLabel = (score: number) => {
    if (score >= 0.9) return 'Отличное соответствие'
    if (score >= 0.7) return 'Хорошее соответствие'
    if (score >= 0.5) return 'Удовлетворительное соответствие'
    return 'Критическое несоответствие'
  }

  return (
    <div className={`bg-white rounded-xl shadow-lg overflow-hidden ${className}`}>
      {/* Заголовок */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Проверка карточки #{check.nm_id}
            </h2>
            {productName && (
              <p className="text-gray-600 mt-1">{productName}</p>
            )}
            <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
              <Clock size={16} />
              <span>Проверено: {formatDate(check.check_timestamp)}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleCopyRecommendations}
              className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <Copy size={18} />
              Скопировать рекомендации
            </button>
            <button
              onClick={handleExportPDF}
              className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <Download size={18} />
              Экспорт PDF
            </button>
            {onRecheck && (
              <button
                onClick={onRecheck}
                className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <RefreshCw size={18} />
                Повторить проверку
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Общая оценка */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="flex-shrink-0">
            <div className={`text-5xl font-bold ${getScoreColor(check.compliance_score.value)}`}>
              {Math.round(check.compliance_score.value * 100)}/100
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900">
              {getScoreLabel(check.compliance_score.value)}
            </h3>
            <p className="text-gray-600 mt-2">{check.compliance_score.interpretation}</p>
            <div className="mt-4">
              <RiskBadge
                level={check.is_compliant ? 'none' : 'high'}
                label={check.is_compliant ? 'Соответствует требованиям' : 'Требует доработки'}
                size="lg"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{check.risks.length}</div>
              <div className="text-sm text-gray-600">Всего рисков</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{check.high_severity_risks.length}</div>
              <div className="text-sm text-gray-600">Высоких рисков</div>
            </div>
          </div>
        </div>
      </div>

      {/* Критические риски */}
      {check.high_severity_risks.length > 0 && (
        <div className="p-6 border-b border-red-200 bg-red-50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={24} />
              <h3 className="text-xl font-semibold text-red-900">Критические риски</h3>
            </div>
            <button
              onClick={() => toggleSection('risks')}
              className="text-red-700 hover:text-red-900"
            >
              {expandedSections.risks ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
          
          {expandedSections.risks && (
            <div className="space-y-4">
              {check.high_severity_risks.map((risk, index) => (
                <div key={index} className="p-4 bg-white border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      <RiskBadge level="high" size="sm" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{risk.type}</h4>
                      <p className="text-gray-700 mt-1">{risk.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <FileText size={14} className="text-gray-400" />
                        <span className="text-sm text-gray-500">Источник: {risk.source}</span>
                        {risk.confidence !== undefined && (
                          <span className="text-sm text-gray-500">
                            • Уверенность: {Math.round(risk.confidence * 100)}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Все риски */}
      {check.risks.length > 0 && (
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">Все риски</h3>
            <button
              onClick={() => toggleSection('risks')}
              className="text-gray-600 hover:text-gray-900"
            >
              {expandedSections.risks ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
          
          {expandedSections.risks && <RiskList risks={check.risks} maxItems={10} />}
        </div>
      )}

      {/* Рекомендации */}
      {check.recommendations.length > 0 && (
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="text-green-600" size={24} />
              <h3 className="text-xl font-semibold text-gray-900">Рекомендации</h3>
            </div>
            <button
              onClick={() => toggleSection('recommendations')}
              className="text-gray-600 hover:text-gray-900"
            >
              {expandedSections.recommendations ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
          
          {expandedSections.recommendations && (
            <ul className="space-y-3">
              {check.recommendations.map((rec, index) => (
                <li key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="mt-1">
                    <div className="w-6 h-6 bg-green-100 text-green-800 rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                  </div>
                  <p className="text-gray-800">{rec}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Требуемые действия */}
      {check.action_required.length > 0 && (
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">Требуемые действия</h3>
            <button
              onClick={() => toggleSection('actions')}
              className="text-gray-600 hover:text-gray-900"
            >
              {expandedSections.actions ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
          
          {expandedSections.actions && (
            <div className="space-y-4">
              {check.action_required.map((action, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    action.priority === 'urgent'
                      ? 'bg-red-50 border-red-200'
                      : action.priority === 'recommended'
                      ? 'bg-yellow-50 border-yellow-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      {action.priority === 'urgent' && (
                        <AlertTriangle className="text-red-600" size={20} />
                      )}
                      {action.priority === 'recommended' && (
                        <CheckCircle className="text-yellow-600" size={20} />
                      )}
                      {action.priority === 'optional' && (
                        <FileText className="text-blue-600" size={20} />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{action.description}</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          action.priority === 'urgent'
                            ? 'bg-red-100 text-red-800'
                            : action.priority === 'recommended'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {action.priority === 'urgent' ? 'Срочно' :
                           action.priority === 'recommended' ? 'Рекомендуется' : 'Опционально'}
                        </span>
                      </div>
                      <p className="text-gray-600 mt-1 text-sm">{action.reason}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Источники из оферты Wildberries */}
      {check.sources && check.sources.length > 0 && (
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="text-indigo-600" size={20} />
            <h3 className="text-xl font-semibold text-gray-900">Источники из оферты Wildberries</h3>
          </div>
          <div className="space-y-3">
            {check.sources.map((source, index) => (
              <div
                key={index}
                className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-1 bg-indigo-600 text-white text-xs font-medium rounded">
                      {source.section}
                    </span>
                    {source.page && (
                      <span className="text-xs text-gray-600">
                        Стр. {source.page}
                      </span>
                    )}
                  </div>
                  {source.relevance !== undefined && (
                    <span className="text-xs text-gray-500 shrink-0 ml-2">
                      Релевантность: {Math.round(source.relevance * 100)}%
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-700 italic line-clamp-4">
                  &ldquo;{source.text}&rdquo;
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Техническая информация */}
      <div className="p-6">
        <button
          onClick={() => toggleSection('technical')}
          className="flex items-center justify-between w-full text-left"
        >
          <h3 className="text-lg font-medium text-gray-900">Техническая информация</h3>
          {expandedSections.technical ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        
        {expandedSections.technical && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Время проверки:</span>
                <span className="font-medium">{formatDate(check.check_timestamp)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Статус соответствия:</span>
                <span className={`font-medium ${check.is_compliant ? 'text-green-600' : 'text-red-600'}`}>
                  {check.is_compliant ? 'Соответствует' : 'Не соответствует'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Всего нарушений:</span>
                <span className="font-medium">{check.violations.length}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Оценка соответствия:</span>
                <span className="font-medium">{check.compliance_score.value.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Риски обнаружены:</span>
                <span className="font-medium">{check.has_risks ? 'Да' : 'Нет'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Высоких рисков:</span>
                <span className="font-medium">{check.high_severity_risks.length}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Краткое резюме */}
      <div className="p-6 bg-gray-50 border-t border-gray-200">
        <h4 className="font-medium text-gray-900 mb-2">Краткое резюме</h4>
        <p className="text-gray-700">{check.summary.detailed}</p>
      </div>
    </div>
  )
}